// The shares endpoints (/shares.json): who a page is shared with, what has been
// offered to me, and the answers to those offers.
//
// These go through `deviseFetch` rather than `redux/client.js`, for two reasons:
// client.js returns `undefined` for any status >= 400, which throws away the
// 422 `{errors: {field: [messages]}}` body that a share's failure carries and
// that the form has to show under its field; and it sends no CSRF token of its
// own, leaving each caller to remember to put an `authenticity_token` in the
// body. `deviseFetch` never throws, stores the token from the response and
// reports a stale one, and each function here sends the token in the
// X-CSRF-Token header.
//
// Every one of these returns a `deviseFetch` result: `{response, json,
// failure}`. The caller reads `response.status`, so it can tell a validation
// failure (422 with `errors`) from anything else.

import { deviseFetch } from '../../../components/devise/deviseRequest'

const readHeaders = () => ({
  Accept: 'application/json',
})

const writeHeaders = (authToken) => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  ...(authToken ? { 'X-CSRF-Token': authToken } : {}),
})

// Every share I am part of: the ones on my pages and the ones that name me as a
// guest, told apart by `is_mine`.
export function fetchShares(dispatch) {
  return deviseFetch(dispatch, '/shares.json', {
    headers: readHeaders(),
    credentials: 'same-origin',
  })
}

export function fetchShare(dispatch, shareId) {
  return deviseFetch(dispatch, `/shares/${shareId}.json`, {
    headers: readHeaders(),
    credentials: 'same-origin',
  })
}

// `share` names its guest by login or email: {page_id, guest_login_or_email}.
export function createShare(dispatch, authToken, share) {
  return deviseFetch(dispatch, '/shares.json', {
    method: 'POST',
    headers: writeHeaders(authToken),
    credentials: 'same-origin',
    body: JSON.stringify({ share }),
  })
}

// Answering an offer: the guest a share names says yes or no. Both take no body
// at all — the server's `accept` and `decline` read no parameters, so nothing
// about the share can ride along with an answer — and both answer 200 with the
// share in its new state.
export function acceptShare(dispatch, authToken, shareId) {
  return deviseFetch(dispatch, `/shares/${shareId}/accept.json`, {
    method: 'PATCH',
    headers: writeHeaders(authToken),
    credentials: 'same-origin',
  })
}

export function declineShare(dispatch, authToken, shareId) {
  return deviseFetch(dispatch, `/shares/${shareId}/decline.json`, {
    method: 'PATCH',
    headers: writeHeaders(authToken),
    credentials: 'same-origin',
  })
}

export function updateShare(dispatch, authToken, shareId, share) {
  return deviseFetch(dispatch, `/shares/${shareId}.json`, {
    method: 'PUT',
    headers: writeHeaders(authToken),
    credentials: 'same-origin',
    body: JSON.stringify({ share }),
  })
}

export function destroyShare(dispatch, authToken, shareId) {
  return deviseFetch(dispatch, `/shares/${shareId}.json`, {
    method: 'DELETE',
    headers: writeHeaders(authToken),
    credentials: 'same-origin',
  })
}
